/**
 * BillingWelcome.jsx — Post-checkout confirmation page.
 *
 * Landing target for the checkout success redirect: backend's
 * POST /api/billing/checkout/session defaults success_url to
 * `${PUBLIC_BASE_URL}/billing/welcome` (see server.py). No frontend
 * route matched that path, so a successful payment ended on the
 * app's 404 page. This is the missing route.
 */

import { Link } from "react-router-dom";
import { ArrowRight, Home, User } from "lucide-react";

export default function BillingWelcome() {
  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-6 py-16"
      data-testid="billing-welcome-page"
    >
      <div className="max-w-xl text-center">
        <p className="aurin-eyebrow mb-3">Payment received</p>
        <h1 className="aurin-display text-3xl md:text-4xl mb-4 leading-tight">
          Thank you. You're all set.
        </h1>
        <p className="text-[15px] text-[hsl(var(--aurin-text-muted))] leading-relaxed mb-8">
          Your purchase went through. Access is tied to your account, so
          it will be there whenever you sign back in.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/"
            data-testid="billing-welcome-home"
            className="aurin-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <Home size={16} className="text-[hsl(var(--aurin-amber))] mb-2" />
            <p className="text-[14px] font-medium text-[hsl(var(--aurin-text))]">Home</p>
            <p className="text-[12px] text-[hsl(var(--aurin-text-muted))] mt-1">
              Back to the house.
            </p>
          </Link>
          <Link
            to="/account"
            data-testid="billing-welcome-account"
            className="aurin-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <User size={16} className="text-[hsl(var(--aurin-amber))] mb-2" />
            <p className="text-[14px] font-medium text-[hsl(var(--aurin-text))]">Your account</p>
            <p className="text-[12px] text-[hsl(var(--aurin-text-muted))] mt-1">
              See what you now have access to.
            </p>
          </Link>
        </div>
        <p className="mt-8 text-[12.5px] italic text-[hsl(var(--aurin-text-muted))]">
          If anything looks wrong,
          <Link to="/reach-out" className="aurin-link mx-1">reach out</Link>
          and we'll sort it. <ArrowRight size={12} className="inline-block" />
        </p>
      </div>
    </div>
  );
}
